import { useCachedMembers } from '@easy-expense/data-firestore-client';
import { EntityFilter, NO_REPORT_FILTER } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { MRT_ColumnFiltersState } from 'material-react-table';

export const useConvertEntityFilterToColumnFilters = () => {
  const locations = Data.locations.use();
  const vendorValues = Data.vendors.use();
  const members = useCachedMembers();
  const categoryValues = Data.expenseCategories.use();
  const tripClassValues = Data.tripClasses.use();
  const paymentMethodValues = Data.paymentMethods.use();
  const reports = Data.reports.use();

  const convert = (entityFilter: EntityFilter) => {
    const columns: MRT_ColumnFiltersState = [];
    let startDate: string | null = null;
    let endDate: string | null = null;
    let maxTotal: number | null = null;
    let minTotal: number | null = null;

    Object.keys(entityFilter).forEach((key: string) => {
      const filter = entityFilter[key as keyof EntityFilter];
      if (filter && key !== 'setEntityFilter') {
        // A saved filter can point at a suggestion that has since been deleted. Those are still
        // offered in the filter dropdowns while transactions use them, so the name has to resolve
        // by key too — otherwise the filter silently stops being applied.
        const vendorName = (
          vendorValues.find((c) => entityFilter.vendors?.includes(c.key)) ??
          Data.vendors.getByKey(entityFilter.vendors?.[0])
        )?.value.name;
        const locationName = (
          locations.find((l) => entityFilter.locations?.includes(l.key)) ??
          Data.locations.getByKey(entityFilter.locations?.[0])
        )?.value.name;
        const filteredMembers = members.filter((m) => entityFilter.createdBy?.includes(m.key));

        const memberNames = filteredMembers
          ? filteredMembers.map((m) => m.displayName?.toLowerCase())
          : [];
        const isNoReportFilter = entityFilter.reports?.includes(NO_REPORT_FILTER);
        const reportName = isNoReportFilter
          ? null
          : reports.find((m) => entityFilter.reports?.includes(m.key))?.name;
        const categoryName = (
          categoryValues.find((c) => entityFilter.categories?.includes(c.key)) ??
          Data.expenseCategories.getByKey(entityFilter.categories?.[0])
        )?.value.name;
        const tripClassName = (
          tripClassValues.find((c) => entityFilter.tripClasses?.includes(c.key)) ??
          Data.tripClasses.getByKey(entityFilter.tripClasses?.[0])
        )?.value.name;
        const paymentMethodName = (
          paymentMethodValues.find((c) => entityFilter.paymentMethods?.includes(c.key)) ??
          Data.paymentMethods.getByKey(entityFilter.paymentMethods?.[0])
        )?.value.name;
        switch (key) {
          case 'startDate':
            startDate = filter.toString();
            break;
          case 'endDate':
            endDate = filter.toString();
            break;
          case 'vendors':
            if (vendorName) {
              columns.push({ id: 'vendorValueName', value: vendorName });
            }
            break;
          case 'locations':
            if (locationName) {
              columns.push({ id: 'trip', value: locationName });
            }
            break;
          case 'createdBy':
            if (memberNames) {
              columns.push({ id: 'creatorDisplayName', value: memberNames });
            }
            break;
          case 'reports':
            if (isNoReportFilter) {
              columns.push({ id: 'reports', value: 'No Report' });
            } else if (reportName) {
              columns.push({ id: 'reports', value: reportName });
            }
            break;
          case 'categories':
            if (categoryName) {
              columns.push({ id: 'categoryValueName', value: [categoryName] });
            }
            break;
          case 'tripClasses':
            if (tripClassName) {
              columns.push({ id: 'tripClassValueName', value: [tripClassName] });
            }
            break;
          case 'paymentMethods':
            if (paymentMethodName) {
              columns.push({ id: 'paymentMethodName', value: [paymentMethodName] });
            }
            break;
          case 'vehicles':
            if (filter) {
              columns.push({ id: 'vehicleValueName', value: filter });
            }
            break;
          case 'maxTotal':
            maxTotal = filter as number;
            break;
          case 'minTotal':
            minTotal = filter as number;
            break;
        }
      }
    });

    if (maxTotal || minTotal) {
      columns.push({ id: 'total', value: [minTotal ?? '', maxTotal ?? ''] });
    }

    if (startDate || endDate) {
      columns.push({ id: 'date', value: [startDate ?? '', endDate ?? ''] });
    }

    if (entityFilter.isReimbursable !== null) {
      columns.push({
        id: 'isReimbursable',
        value: entityFilter.isReimbursable ? ['true'] : ['false'],
      });
    } else {
      columns.push({
        id: 'isReimbursable',
        value: null,
      });
    }

    if (entityFilter.reimbursableState !== null) {
      columns.push({
        id: 'reimbursableState',
        value: entityFilter.reimbursableState,
      });
    } else {
      columns.push({
        id: 'reimbursableState',
        value: '',
      });
    }

    return columns;
  };
  return convert;
};
